/** Unicode code points that Windows-1252 maps onto bytes 0x80–0x9F. */
const CP1252_BYTE_BY_CODE_POINT: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

/**
 * Undo one pass of “UTF-8 bytes read as Windows-1252”.
 * Returns the input when the text is not that kind of corruption.
 */
function repairCp1252MojibakeOnce(text: string): string {
  const bytes: number[] = [];
  for (const char of text) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      return text;
    }
    if (codePoint < 0x80) {
      bytes.push(codePoint);
      continue;
    }
    const mapped = CP1252_BYTE_BY_CODE_POINT[codePoint];
    if (mapped !== undefined) {
      bytes.push(mapped);
      continue;
    }
    if (codePoint <= 0xff) {
      bytes.push(codePoint);
      continue;
    }
    return text;
  }

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(bytes)
    );
  } catch {
    return text;
  }
}

/** Restore text that was decoded as Windows-1252 instead of UTF-8. */
export function repairCp1252Mojibake(text: string): string {
  const once = repairCp1252MojibakeOnce(text);
  if (once === text) {
    return text;
  }
  const twice = repairCp1252MojibakeOnce(once);
  return twice === once ? once : twice;
}

function foldApostrophes(value: string): string {
  return value.replace(/[\u2018\u2019\u201A\u201B\u2032]/g, '\u0027');
}

/**
 * Sub-campaigns are named `"<name> (<value>)"`. The value can come back
 * with a mangled apostrophe even when the CSV preview showed the real text.
 * Returns the name to save, or null when no change is needed.
 */
export function correctedSubCampaignName(
  createdName: string,
  baseName: string,
  previewValues: string[]
): string | null {
  const prefix = `${baseName} (`;
  if (!createdName.startsWith(prefix) || !createdName.endsWith(')')) {
    return null;
  }
  const rawValue = createdName.slice(prefix.length, -1);
  if (previewValues.includes(rawValue)) {
    return null;
  }
  const repaired = repairCp1252Mojibake(rawValue);
  const match = previewValues.find(
    (value) =>
      value === repaired || foldApostrophes(value) === foldApostrophes(repaired)
  );
  if (!match) {
    return null;
  }
  return `${baseName} (${match})`;
}
